'use client';

import { createClient } from '@/lib/supabase/client';
import type { Pantry, Submission, Report, PantryStatus, DonationHub, PantryPost, HubDonation } from '@/lib/pantryData';

// ─── Type mappers ─────────────────────────────────────────────────────────────

function rowToPantry(row: Record<string, unknown>): Pantry {
  return {
    id: row.id as string,
    pangalan: row.pangalan as string,
    address: row.address as string,
    barangay: row.barangay as string,
    lungsod: row.lungsod as string,
    lat: row.lat as number,
    lng: row.lng as number,
    status: row.pantry_status as PantryStatus,
    items: (row.items as string[]) ?? [],
    oras: row.oras as string,
    contact: row.contact as string,
    nagtatag: row.nagtatag as string,
    hulingUpdate: row.huling_update as string,
    larawan: row.larawan as string | undefined,
    itemAvailability: (row.item_availability as Record<string, import('@/lib/pantryData').AvailabilityLevel>) ?? {},
    bankAccountDetails: (row.bank_account_details as string) ?? '',
    qrCodeUrl: (row.qr_code_url as string | null) ?? undefined,
    facebookUrl: (row.facebook_url as string | null) ?? undefined,
    instagramUrl: (row.instagram_url as string | null) ?? undefined,
    twitterUrl: (row.twitter_url as string | null) ?? undefined,
    messengerUrl: (row.messenger_url as string | null) ?? undefined,
    nearestLandmark: (row.nearest_landmark as string | null) ?? undefined,
    contactPerson: (row.contact_person as string | null) ?? undefined,
    pantryPassword: (row.pantry_password as string | null) ?? undefined,
    description: (row.description as string | null) ?? undefined,
    photoUrl: (row.photo_url as string | null) ?? undefined,
    howItStarted: (row.how_it_started as string | null) ?? undefined,
    scheduleType: ((row.schedule_type as string | null) ?? 'fixed') as 'fixed' | 'varies',
    scheduleDetails: (row.schedule_details as string | null) ?? undefined,
  };
}

function rowToSubmission(row: Record<string, unknown>): Submission {
  return {
    id: row.id as string,
    pangalan: row.pangalan as string,
    address: row.address as string,
    barangay: row.barangay as string,
    lungsod: row.lungsod as string,
    lat: row.lat as number,
    lng: row.lng as number,
    items: (row.items as string[]) ?? [],
    oras: row.oras as string,
    contact: row.contact as string,
    nagtatag: row.nagtatag as string,
    nagpadala: row.nagpadala as string,
    emailNagpadala: row.email_nagpadala as string,
    petsa: row.petsa as string,
    status: row.sub_status as Submission['status'],
    nota: (row.nota as string) ?? '',
    bankAccountDetails: (row.bank_account_details as string) ?? '',
    qrCodeUrl: (row.qr_code_url as string | null) ?? undefined,
    facebookUrl: (row.facebook_url as string | null) ?? undefined,
    instagramUrl: (row.instagram_url as string | null) ?? undefined,
    twitterUrl: (row.twitter_url as string | null) ?? undefined,
    messengerUrl: (row.messenger_url as string | null) ?? undefined,
    nearestLandmark: (row.nearest_landmark as string | null) ?? undefined,
    contactPerson: (row.contact_person as string | null) ?? undefined,
    description: (row.description as string | null) ?? undefined,
    photoUrl: (row.photo_url as string | null) ?? undefined,
    howItStarted: (row.how_it_started as string | null) ?? undefined,
    scheduleType: ((row.schedule_type as string | null) ?? 'fixed') as 'fixed' | 'varies',
    scheduleDetails: (row.schedule_details as string | null) ?? undefined,
  };
}

function rowToReport(row: Record<string, unknown>): Report {
  return {
    id: row.id as string,
    pantryId: row.pantry_id as string,
    pantryPangalan: row.pantry_pangalan as string,
    uri: row.uri as Report['uri'],
    detalye: row.detalye as string,
    nagulat: row.nagulat as string,
    petsa: row.petsa as string,
    status: row.report_status as Report['status'],
  };
}

function rowToHub(row: Record<string, unknown>): DonationHub {
  return {
    id: row.id as string,
    name: row.name as string,
    photoUrl: (row.photo_url as string | null) ?? undefined,
    location: (row.location as string | null) ?? undefined,
    contact: (row.contact as string | null) ?? undefined,
    email: (row.email as string | null) ?? undefined,
    representativeName: (row.representative_name as string | null) ?? undefined,
    qrCodeUrl: (row.qr_code_url as string | null) ?? undefined,
    description: (row.description as string | null) ?? undefined,
    createdAt: row.created_at as string,
    hubPassword: (row.hub_password as string | null) ?? undefined,
  };
}

function rowToPost(row: Record<string, unknown>): PantryPost {
  const photoUrls = (row.photo_urls as string[] | null) ?? [];
  const singlePhotoUrl = (row.photo_url as string | null) ?? undefined;
  // Merge: prefer photo_urls array, fall back to single photo_url
  const allPhotos = photoUrls.length > 0 ? photoUrls : (singlePhotoUrl ? [singlePhotoUrl] : []);
  return {
    id: row.id as string,
    pantryId: row.pantry_id as string,
    content: row.content as string,
    photoUrl: allPhotos[0] ?? undefined,
    photoUrls: allPhotos.length > 0 ? allPhotos : undefined,
    createdAt: row.created_at as string,
  };
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

export async function fetchPantriesFromSupabase(): Promise<Pantry[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantries')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchPantries error:', error.message); return []; }
  return (data ?? []).map(rowToPantry);
}

export async function fetchSubmissionsFromSupabase(): Promise<Submission[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_submissions')
    .select('*')
    .order('petsa', { ascending: false });
  if (error) { console.error('fetchSubmissions error:', error.message); return []; }
  return (data ?? []).map(rowToSubmission);
}

export async function fetchReportsFromSupabase(): Promise<Report[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_reports')
    .select('*')
    .order('petsa', { ascending: false });
  if (error) { console.error('fetchReports error:', error.message); return []; }
  return (data ?? []).map(rowToReport);
}

export async function fetchDonationHubs(): Promise<DonationHub[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('donation_hubs')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchDonationHubs error:', error.message); return []; }
  return (data ?? []).map(rowToHub);
}

export async function fetchPantryPosts(pantryId: string): Promise<PantryPost[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_posts')
    .select('*')
    .eq('pantry_id', pantryId)
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchPantryPosts error:', error.message); return []; }
  return (data ?? []).map(rowToPost);
}

export async function fetchHubForPantry(pantryId: string): Promise<DonationHub | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_hub_assignments')
    .select('hub_id')
    .eq('pantry_id', pantryId)
    .single();
  if (error || !data) return null;
  const { data: hub, error: hubError } = await supabase
    .from('donation_hubs')
    .select('*')
    .eq('id', data.hub_id)
    .single();
  if (hubError || !hub) return null;
  return rowToHub(hub as Record<string, unknown>);
}

export async function fetchPantriesForHub(hubId: string): Promise<string[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_hub_assignments')
    .select('pantry_id')
    .eq('hub_id', hubId);
  if (error) return [];
  return (data ?? []).map((r: Record<string, unknown>) => r.pantry_id as string);
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function updateSubmissionInSupabase(
  id: string,
  patch: { sub_status: string; nota: string }
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('pantry_submissions')
    .update(patch)
    .eq('id', id);
  if (error) console.error('updateSubmission error:', error.message);
}

export async function insertPantryToSupabase(pantry: Pantry): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('pantries').upsert(
    {
      id: pantry.id,
      pangalan: pantry.pangalan,
      address: pantry.address,
      barangay: pantry.barangay,
      lungsod: pantry.lungsod,
      lat: pantry.lat,
      lng: pantry.lng,
      pantry_status: pantry.status,
      items: pantry.items,
      oras: pantry.oras,
      contact: pantry.contact,
      nagtatag: pantry.nagtatag,
      huling_update: pantry.hulingUpdate,
      larawan: pantry.larawan ?? null,
      bank_account_details: pantry.bankAccountDetails ?? '',
      qr_code_url: pantry.qrCodeUrl ?? null,
      facebook_url: pantry.facebookUrl ?? null,
      instagram_url: pantry.instagramUrl ?? null,
      twitter_url: pantry.twitterUrl ?? null,
      messenger_url: pantry.messengerUrl ?? null,
      nearest_landmark: pantry.nearestLandmark ?? null,
      contact_person: pantry.contactPerson ?? null,
      pantry_password: pantry.pantryPassword ?? null,
      description: pantry.description ?? null,
      photo_url: pantry.photoUrl ?? null,
      how_it_started: pantry.howItStarted ?? null,
      schedule_type: pantry.scheduleType ?? 'fixed',
      schedule_details: pantry.scheduleDetails ?? null,
    },
    { onConflict: 'id' }
  );
  if (error) console.error('insertPantry error:', error.message);
}

export async function updatePantryInSupabase(
  id: string,
  patch: { pantry_status?: string; huling_update?: string; item_availability?: Record<string, string>; pantry_password?: string | null }
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('pantries')
    .update(patch)
    .eq('id', id);
  if (error) console.error('updatePantry error:', error.message);
}

export async function deletePantryFromSupabase(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('pantries').delete().eq('id', id);
  if (error) console.error('deletePantry error:', error.message);
}

export async function insertSubmissionToSupabase(submission: Omit<Submission, 'id'>): Promise<{ id: string } | null> {
  const supabase = createClient();
  const newId = `sub-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const { data, error } = await supabase
    .from('pantry_submissions')
    .insert({
      id: newId,
      pangalan: submission.pangalan,
      address: submission.address,
      barangay: submission.barangay,
      lungsod: submission.lungsod,
      lat: submission.lat,
      lng: submission.lng,
      items: submission.items,
      oras: submission.oras,
      contact: submission.contact,
      nagtatag: submission.nagtatag,
      nagpadala: submission.nagpadala,
      email_nagpadala: submission.emailNagpadala,
      petsa: submission.petsa,
      sub_status: submission.status,
      nota: submission.nota ?? '',
      bank_account_details: submission.bankAccountDetails ?? '',
      qr_code_url: submission.qrCodeUrl ?? null,
      facebook_url: submission.facebookUrl ?? null,
      instagram_url: submission.instagramUrl ?? null,
      twitter_url: submission.twitterUrl ?? null,
      messenger_url: submission.messengerUrl ?? null,
      nearest_landmark: submission.nearestLandmark ?? null,
      contact_person: submission.contactPerson ?? null,
      description: submission.description ?? null,
      photo_url: submission.photoUrl ?? null,
      how_it_started: submission.howItStarted ?? null,
      schedule_type: submission.scheduleType ?? 'fixed',
      schedule_details: submission.scheduleDetails ?? null,
    })
    .select('id')
    .single();

  if (error) {
    console.error('insertSubmission error:', error.message, error.details, error.hint);
    return null;
  }
  return data as { id: string };
}

export async function updateReportInSupabase(
  id: string,
  patch: { report_status: string }
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('pantry_reports')
    .update(patch)
    .eq('id', id);
  if (error) console.error('updateReport error:', error.message);
}

// ─── Donation Hub CRUD ────────────────────────────────────────────────────────

export async function insertDonationHub(hub: Omit<DonationHub, 'id' | 'createdAt'>): Promise<DonationHub | null> {
  const supabase = createClient();
  const newId = `hub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const { data, error } = await supabase
    .from('donation_hubs')
    .insert({
      id: newId,
      name: hub.name,
      photoUrl: hub.photoUrl ?? null,
      location: hub.location ?? null,
      contact: hub.contact ?? null,
      email: hub.email ?? null,
      representativeName: hub.representativeName ?? null,
      qrCodeUrl: hub.qrCodeUrl ?? null,
      description: hub.description ?? null,
    })
    .select('*')
    .single();
  if (error) { console.error('insertDonationHub error:', error.message); return null; }
  return rowToHub(data as Record<string, unknown>);
}

export async function updateDonationHub(id: string, hub: Partial<Omit<DonationHub, 'id' | 'createdAt'>>): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('donation_hubs')
    .update({
      name: hub.name,
      photoUrl: hub.photoUrl ?? null,
      location: hub.location ?? null,
      contact: hub.contact ?? null,
      email: hub.email ?? null,
      representativeName: hub.representativeName ?? null,
      qrCodeUrl: hub.qrCodeUrl ?? null,
      description: hub.description ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) console.error('updateDonationHub error:', error.message);
}

export async function deleteDonationHub(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('donation_hubs').delete().eq('id', id);
  if (error) console.error('deleteDonationHub error:', error.message);
}

export async function assignPantryToHub(pantryId: string, hubId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('pantry_hub_assignments')
    .upsert({ pantry_id: pantryId, hub_id: hubId }, { onConflict: 'pantry_id' });
  if (error) console.error('assignPantryToHub error:', error.message);
}

export async function removePantryFromHub(pantryId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('pantry_hub_assignments')
    .delete()
    .eq('pantry_id', pantryId);
  if (error) console.error('removePantryFromHub error:', error.message);
}

// ─── Pantry Posts ─────────────────────────────────────────────────────────────

export async function insertPantryPost(pantryId: string, content: string, photoUrls?: string[]): Promise<PantryPost | null> {
  const supabase = createClient();
  const newId = `post-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const firstPhoto = photoUrls && photoUrls.length > 0 ? photoUrls[0] : null;
  const { data, error } = await supabase
    .from('pantry_posts')
    .insert({
      id: newId,
      pantry_id: pantryId,
      content,
      photo_url: firstPhoto,
      photo_urls: photoUrls ?? [],
    })
    .select('*')
    .single();
  if (error) { console.error('insertPantryPost error:', error.message); return null; }

  // Notify admin of new post
  try {
    const { data: pantryData } = await supabase.from('pantries').select('pangalan').eq('id', pantryId).single();
    const pantryName = (pantryData as Record<string, unknown>)?.pangalan as string ?? 'Unknown Pantry';
    await sendPantryEmail('new_post' as Parameters<typeof sendPantryEmail>[0], 'tulongtayo.online@gmail.com', {
      pantryName,
      postType: 'Post',
      content: content.slice(0, 200),
    });
  } catch { /* non-blocking */ }

  return rowToPost(data as Record<string, unknown>);
}

export async function deletePantryPost(postId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('pantry_posts').delete().eq('id', postId);
  if (error) console.error('deletePantryPost error:', error.message);
}

// ─── Hub Donations ────────────────────────────────────────────────────────────

export async function insertHubDonation(donation: Omit<HubDonation, 'id' | 'createdAt' | 'status'>): Promise<HubDonation | null> {
  const supabase = createClient();
  const newId = `hdon-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const { data, error } = await supabase
    .from('hub_donations')
    .insert({
      id: newId,
      hubId: donation.hubId,
      donorName: donation.donorName,
      donorEmail: donation.donorEmail ?? null,
      donorContact: donation.donorContact,
      items: donation.items ?? null,
      message: donation.message ?? null,
      paymentMethod: donation.paymentMethod ?? null,
      paymentScreenshotUrl: donation.paymentScreenshotUrl ?? null,
      status: 'nakabinbin',
    })
    .select('*')
    .single();
  if (error) { console.error('insertHubDonation error:', error.message); return null; }
  const r = data as Record<string, unknown>;
  return {
    id: r.id as string,
    hubId: r.hub_id as string,
    donorName: r.donor_name as string,
    donorEmail: (r.donor_email as string | null) ?? undefined,
    donorContact: r.donor_contact as string,
    items: (r.items as string | null) ?? undefined,
    message: (r.message as string | null) ?? undefined,
    paymentMethod: (r.payment_method as string | null) ?? undefined,
    paymentScreenshotUrl: (r.payment_screenshot_url as string | null) ?? undefined,
    status: (r.status as HubDonation['status']) ?? 'nakabinbin',
    createdAt: r.created_at as string,
  };
}

// ─── Real-time subscriptions ──────────────────────────────────────────────────

export function subscribeToAdminRealtime(callbacks: {
  onPantryChange: (pantry: Pantry, eventType: string) => void;
  onPantryDelete: (id: string) => void;
  onSubmissionChange: (submission: Submission) => void;
  onReportChange: (report: Report) => void;
}): () => void {
  const supabase = createClient();

  const channel = supabase
    .channel('admin_realtime_all')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'pantries' },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          const old = payload.old as Record<string, unknown>;
          callbacks.onPantryDelete(old.id as string);
        } else {
          callbacks.onPantryChange(
            rowToPantry(payload.new as Record<string, unknown>),
            payload.eventType
          );
        }
      }
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'pantry_submissions' },
      (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          callbacks.onSubmissionChange(
            rowToSubmission(payload.new as Record<string, unknown>)
          );
        }
      }
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'pantry_reports' },
      (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          callbacks.onReportChange(
            rowToReport(payload.new as Record<string, unknown>)
          );
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ─── Email Helper ─────────────────────────────────────────────────────────────

export async function sendPantryEmail(
  type: 'submission_received' | 'submission_approved' | 'donation_received' | 'report_resolved' | 'hub_donation_received' | 'hub_donation_donor_confirmation',
  to: string,
  data: Record<string, string>
): Promise<void> {
  try {
    const supabase = createClient();
    const { error } = await supabase.functions.invoke('send-email', {
      body: { type, to, data },
    });
    if (error) console.error('sendPantryEmail error:', error.message);
  } catch (err) {
    console.error('sendPantryEmail exception:', err);
  }
}

// ─── Password Generator ───────────────────────────────────────────────────────

export function generatePantryPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '!@#$';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars[Math.floor(Math.random() * chars.length)];
  }
  password += specials[Math.floor(Math.random() * specials.length)];
  password += Math.floor(Math.random() * 9) + 1;
  // Shuffle
  return password.split('').sort(() => Math.random() - 0.5).join('');
}
